<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex items-center justify-between mb-2">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Customer Access</h1>
      <button @click="openCreate"
        class="px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors">
        + New Grant
      </button>
    </div>
    <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
      Grant named customers token-based access to private SBOMs and/or the vulnerability feed.
      Tokens can be shared via any channel — no email required.
    </p>

    <!-- Filter -->
    <div class="flex gap-2 mb-4">
      <button v-for="s in ['all','pending','approved','revoked']" :key="s"
        @click="statusFilter = s; fetchGrants()"
        class="px-3 py-1 text-xs font-medium rounded-full border transition-colors"
        :class="statusFilter === s
          ? 'bg-blue-600 text-white border-blue-600'
          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'">
        {{ s.charAt(0).toUpperCase() + s.slice(1) }}
      </button>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-16">
      <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-700 border-t-blue-500"></div>
    </div>

    <div v-else-if="grants.length === 0" class="text-center py-16 text-gray-400 dark:text-gray-500 text-sm">
      No grants yet.
    </div>

    <div v-else class="space-y-3">
      <div v-for="g in grants" :key="g.id"
        class="card px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div class="flex items-start gap-3 min-w-0 flex-1">
          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0 mt-0.5"
            :class="statusClass(g.status)">{{ g.status }}</span>
          <div class="min-w-0">
            <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
              {{ g.customer_name }}
              <span v-if="g.company" class="font-normal text-gray-500 dark:text-gray-400"> · {{ g.company }}</span>
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ g.customer_email }}</p>
            <div class="flex gap-2 mt-1 flex-wrap">
              <span v-if="g.scope_sboms" class="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">SBOMs</span>
              <span v-if="g.scope_vulns" class="text-xs bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-1.5 py-0.5 rounded">Vulns</span>
              <span class="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded">
                {{ g.crm_source === 'manual' ? 'Manual' : g.crm_source }}
              </span>
              <span v-if="g.expires_at" class="text-xs text-gray-400 dark:text-gray-500">
                Expires {{ formatDate(g.expires_at) }}
              </span>
              <span v-if="g.usage_count > 0" class="text-xs text-gray-400 dark:text-gray-500">
                Used {{ g.usage_count }}× · last {{ formatDate(g.last_used_at) }}
              </span>
              <span v-else-if="g.status === 'approved'" class="text-xs text-gray-400 dark:text-gray-500 italic">
                Never used
              </span>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
          <template v-if="g.status === 'pending'">
            <button @click="approve(g)" :disabled="actionLoading === g.id"
              class="px-3 py-1.5 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors">
              {{ actionLoading === g.id ? '…' : 'Approve' }}
            </button>
            <button @click="deleteTarget = g"
              class="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              Delete
            </button>
          </template>

          <template v-else-if="g.status === 'approved'">
            <button @click="showToken(g)"
              class="px-3 py-1.5 text-xs font-medium rounded-md border border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
              Copy URLs
            </button>
            <button @click="rotateToken(g)" :disabled="actionLoading === g.id"
              class="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors">
              Rotate
            </button>
            <button @click="revokeTarget = g"
              class="px-3 py-1.5 text-xs font-medium rounded-md text-red-700 dark:text-red-300 border border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              Revoke
            </button>
          </template>

          <template v-else-if="g.status === 'revoked'">
            <button @click="deleteTarget = g"
              class="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              Delete
            </button>
          </template>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex items-center justify-center gap-2 mt-6">
      <button @click="fetchGrants(currentPage - 1)" :disabled="currentPage <= 1"
        class="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-md disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">Prev</button>
      <span class="text-sm text-gray-500 dark:text-gray-400">{{ currentPage }} / {{ totalPages }}</span>
      <button @click="fetchGrants(currentPage + 1)" :disabled="currentPage >= totalPages"
        class="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-md disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">Next</button>
    </div>

    <!-- ── Create modal ──────────────────────────────────────────────────── -->
    <div v-if="createModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6 relative">
        <button @click="createModal = false" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none">&times;</button>
        <h2 class="text-lg font-bold text-gray-900 dark:text-white mb-4">New Customer Access Grant</h2>

        <div class="space-y-3">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Name *</label>
              <input v-model="form.customer_name" type="text" placeholder="Jane Smith"
                class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Email *</label>
              <input v-model="form.customer_email" type="email" placeholder="jane@acme.com"
                class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            </div>
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Company</label>
            <input v-model="form.company" type="text" placeholder="Acme Corp"
              class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Access scope</label>
            <div class="flex gap-4">
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" v-model="form.scope_sboms" class="rounded border-gray-300 dark:border-gray-600 text-blue-600">
                <span class="text-sm text-gray-700 dark:text-gray-300">Private SBOMs</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" v-model="form.scope_vulns" class="rounded border-gray-300 dark:border-gray-600 text-blue-600">
                <span class="text-sm text-gray-700 dark:text-gray-300">Vulnerability feed</span>
              </label>
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Restrict to specific products
              <span class="font-normal text-gray-400">(optional — leave all unchecked for full access)</span>
            </label>
            <div v-if="productsLoading" class="text-xs text-gray-400 py-2">Loading products…</div>
            <div v-else-if="products.length === 0" class="text-xs text-gray-400 py-2">No products found</div>
            <div v-else class="max-h-36 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600 divide-y divide-gray-100 dark:divide-gray-700">
              <label v-for="p in products" :key="p.id"
                class="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                <input type="checkbox" :value="p.id" v-model="form.selectedProductIds"
                  class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500">
                <span class="text-sm text-gray-800 dark:text-gray-200">{{ p.name }}</span>
                <span v-if="p.version" class="text-xs text-gray-400 ml-auto">{{ p.version }}</span>
              </label>
            </div>
            <p v-if="form.selectedProductIds.length > 0" class="mt-1 text-xs text-blue-600 dark:text-blue-400">
              {{ form.selectedProductIds.length }} product{{ form.selectedProductIds.length !== 1 ? 's' : '' }} selected
            </p>
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Expiry
              <span class="font-normal text-gray-400">(optional)</span>
            </label>
            <input v-model="form.expires_at" type="datetime-local"
              class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Internal note</label>
            <input v-model="form.note" type="text" placeholder="e.g. Contract #1234, DORA due diligence"
              class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-6">
          <button @click="createModal = false"
            class="px-4 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            Cancel
          </button>
          <button @click="createGrant" :disabled="createLoading"
            class="px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {{ createLoading ? 'Creating…' : 'Create Grant' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Delete confirmation ──────────────────────────────────────────── -->
  <DeleteConfirmationModal
    :show="!!deleteTarget"
    :title="`Delete grant for ${deleteTarget?.customer_name}?`"
    :message="deleteTarget?.status === 'approved'
      ? 'This will permanently remove the grant. The token will stop working immediately.'
      : 'This will permanently remove the pending grant.'"
    confirm-text="Delete"
    :loading="actionLoading === deleteTarget?.id"
    @confirm="confirmDelete"
    @cancel="deleteTarget = null"
  />

  <!-- ── Revoke confirmation ───────────────────────────────────────────── -->
  <DeleteConfirmationModal
    :show="!!revokeTarget"
    title="Revoke access?"
    :message="`This will revoke ${revokeTarget?.customer_name}'s token immediately. They will lose access to all shared endpoints.`"
    confirm-text="Revoke"
    :loading="actionLoading === revokeTarget?.id"
    @confirm="confirmRevoke"
    @cancel="revokeTarget = null"
  />

  <!-- ── Token / URL modal ─────────────────────────────────────────────── -->
    <div v-if="tokenModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6 relative">
        <button @click="tokenModal = false" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none">&times;</button>
        <h2 class="text-lg font-bold text-gray-900 dark:text-white mb-1">Access endpoints</h2>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Share these URLs directly with <strong class="text-gray-700 dark:text-gray-200">{{ activeGrant?.customer_name }}</strong>.
          No login required — the token is the credential. Rotate the token to invalidate all existing URLs.
        </p>

        <div v-if="!tokenRevealed" class="mb-4 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
          For security, approved tokens are never re-displayed. The URLs below show the shape with a <code class="font-mono">&lt;TOKEN&gt;</code> placeholder. Use <strong>Rotate</strong> on this grant to get a fresh, working token.
        </div>

        <div v-if="activeGrant?.scope_sboms" class="mb-4">
          <p class="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 uppercase tracking-wide">SBOM list</p>
          <div class="flex items-center gap-2">
            <code class="flex-1 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 truncate text-gray-700 dark:text-gray-300 select-all">
              {{ sbomListUrl }}
            </code>
            <button @click="copy(sbomListUrl)" :disabled="!tokenRevealed" class="text-xs text-blue-600 dark:text-blue-400 hover:underline flex-shrink-0 disabled:text-gray-400 disabled:cursor-not-allowed disabled:no-underline">Copy</button>
          </div>
        </div>

        <div v-if="activeGrant?.scope_sboms" class="mb-4">
          <p class="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 uppercase tracking-wide">SBOM download (per SBOM ID)</p>
          <div class="flex items-center gap-2">
            <code class="flex-1 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 truncate text-gray-700 dark:text-gray-300 select-all">
              {{ sbomDownloadUrl }}
            </code>
            <button @click="copy(sbomDownloadUrl)" :disabled="!tokenRevealed" class="text-xs text-blue-600 dark:text-blue-400 hover:underline flex-shrink-0 disabled:text-gray-400 disabled:cursor-not-allowed disabled:no-underline">Copy</button>
          </div>
        </div>

        <div v-if="activeGrant?.scope_vulns" class="mb-4">
          <p class="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 uppercase tracking-wide">Vulnerability feed</p>
          <div class="flex items-center gap-2">
            <code class="flex-1 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 truncate text-gray-700 dark:text-gray-300 select-all">
              {{ vulnFeedUrl }}
            </code>
            <button @click="copy(vulnFeedUrl)" :disabled="!tokenRevealed" class="text-xs text-blue-600 dark:text-blue-400 hover:underline flex-shrink-0 disabled:text-gray-400 disabled:cursor-not-allowed disabled:no-underline">Copy</button>
          </div>
        </div>

        <p class="text-xs text-gray-400 dark:text-gray-500 mt-2">
          CRM integrations (Salesforce, HubSpot) will auto-populate grants in a future release.
          The token mechanism is the same regardless of source.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import axios from 'axios'
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue'

const getAPIURL = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  return envUrl.replace(/\/$/, '').replace(/\/api\/v1$/, '') + '/api/v1'
}

export default {
  name: 'CustomerAccessView',
  components: { DeleteConfirmationModal },
  setup() {
    const store = useStore()
    const route = useRoute()
    const tenantId = computed(() => route.params.tenantId || store.getters.currentTenantId)
    const headers = computed(() => ({ Authorization: `Bearer ${store.state.token}` }))

    const loading = ref(false)
    const createLoading = ref(false)
    const actionLoading = ref(null)
    const grants = ref([])
    const currentPage = ref(1)
    const totalPages = ref(1)
    const statusFilter = ref('all')

    const createModal = ref(false)
    const tokenModal = ref(false)
    const activeGrant = ref(null)
    const activeToken = ref('')
    const deleteTarget = ref(null)
    const revokeTarget = ref(null)
    const products = ref([])
    const productsLoading = ref(false)

    const emptyForm = () => ({
      customer_name: '',
      customer_email: '',
      company: '',
      note: '',
      scope_sboms: true,
      scope_vulns: false,
      selectedProductIds: [],
      expires_at: '',
    })
    const form = ref(emptyForm())

    const fetchGrants = async (page = 1) => {
      if (!tenantId.value) return
      loading.value = true
      try {
        const params = { page, per_page: 20 }
        if (statusFilter.value !== 'all') params.status = statusFilter.value
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/customer-access`, {
          headers: headers.value, params
        })
        const data = res.data
        grants.value = data?.data || data || []
        currentPage.value = data?.currentPage || page
        totalPages.value = data?.totalPages || 1
      } catch (e) {
        console.error('Failed to fetch grants:', e)
      } finally {
        loading.value = false
      }
    }

    const fetchProducts = async () => {
      if (!tenantId.value || products.value.length > 0) return
      productsLoading.value = true
      try {
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/products`, {
          headers: headers.value, params: { per_page: 200 }
        })
        products.value = res.data?.data || res.data || []
      } catch (e) {
        console.error('Failed to fetch products:', e)
      } finally {
        productsLoading.value = false
      }
    }

    const openCreate = () => {
      form.value = emptyForm()
      createModal.value = true
      fetchProducts()
    }

    const createGrant = async () => {
      if (!form.value.customer_name || !form.value.customer_email) {
        store.dispatch('notification/showNotification', { type: 'error', message: 'Name and email are required' })
        return
      }
      if (!form.value.scope_sboms && !form.value.scope_vulns) {
        store.dispatch('notification/showNotification', { type: 'error', message: 'Select at least one access scope' })
        return
      }

      const productIDs = form.value.selectedProductIds.length > 0
        ? JSON.stringify(form.value.selectedProductIds)
        : ''

      createLoading.value = true
      try {
        await axios.post(`${getAPIURL()}/admin/tenants/${tenantId.value}/customer-access`, {
          customer_name: form.value.customer_name,
          customer_email: form.value.customer_email,
          company: form.value.company,
          note: form.value.note,
          scope_sboms: form.value.scope_sboms,
          scope_vulns: form.value.scope_vulns,
          product_ids: productIDs,
          expires_at: form.value.expires_at || null,
          crm_source: 'manual',
        }, { headers: headers.value })
        store.dispatch('notification/showNotification', { type: 'success', message: 'Grant created — approve to issue token' })
        createModal.value = false
        fetchGrants()
      } catch (e) {
        store.dispatch('notification/showNotification', { type: 'error', message: e.response?.data?.error || 'Failed to create grant' })
      } finally {
        createLoading.value = false
      }
    }

    const approve = async (g) => {
      actionLoading.value = g.id
      try {
        const res = await axios.post(`${getAPIURL()}/admin/tenants/${tenantId.value}/customer-access/${g.id}/approve`, {}, { headers: headers.value })
        store.dispatch('notification/showNotification', { type: 'success', message: 'Access approved' })
        // Show the token immediately after approval
        activeGrant.value = { ...g, status: 'approved', ...res.data.grant }
        activeToken.value = res.data.access_token
        tokenModal.value = true
        fetchGrants()
      } catch (e) {
        store.dispatch('notification/showNotification', { type: 'error', message: e.response?.data?.error || 'Failed to approve' })
      } finally {
        actionLoading.value = null
      }
    }

    const confirmRevoke = async () => {
      const g = revokeTarget.value
      if (!g) return
      actionLoading.value = g.id
      try {
        await axios.post(`${getAPIURL()}/admin/tenants/${tenantId.value}/customer-access/${g.id}/revoke`, {}, { headers: headers.value })
        store.dispatch('notification/showNotification', { type: 'success', message: 'Access revoked' })
        revokeTarget.value = null
        fetchGrants()
      } catch (e) {
        store.dispatch('notification/showNotification', { type: 'error', message: 'Failed to revoke' })
      } finally {
        actionLoading.value = null
      }
    }

    const rotateToken = async (g) => {
      actionLoading.value = g.id
      try {
        const res = await axios.post(`${getAPIURL()}/admin/tenants/${tenantId.value}/customer-access/${g.id}/rotate-token`, {}, { headers: headers.value })
        activeGrant.value = g
        activeToken.value = res.data.access_token
        tokenModal.value = true
        store.dispatch('notification/showNotification', { type: 'success', message: 'Token rotated — share new URLs' })
      } catch (e) {
        store.dispatch('notification/showNotification', { type: 'error', message: 'Failed to rotate token' })
      } finally {
        actionLoading.value = null
      }
    }

    const confirmDelete = async () => {
      const g = deleteTarget.value
      if (!g) return
      actionLoading.value = g.id
      try {
        await axios.delete(`${getAPIURL()}/admin/tenants/${tenantId.value}/customer-access/${g.id}`, { headers: headers.value })
        store.dispatch('notification/showNotification', { type: 'success', message: 'Grant deleted' })
        deleteTarget.value = null
        fetchGrants()
      } catch (e) {
        store.dispatch('notification/showNotification', { type: 'error', message: 'Failed to delete' })
      } finally {
        actionLoading.value = null
      }
    }

    const showToken = (g) => {
      activeGrant.value = g
      // Tokens are only visible at approval/rotation time. For existing grants we
      // expose the URL template with a <TOKEN> placeholder so admins can see the
      // shape — they must rotate to obtain a fresh, working token.
      activeToken.value = '<TOKEN>'
      tokenModal.value = true
    }

    const baseUrl = computed(() => {
      const api = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
      return api.replace(/\/$/, '').replace(/\/api\/v1$/, '')
    })

    const tokenValue = computed(() => activeToken.value)
    const tokenRevealed = computed(() =>
      activeToken.value && activeToken.value !== '<TOKEN>'
    )

    const sbomListUrl = computed(() =>
      `${baseUrl.value}/api/v1/tenants/${tenantId.value}/access/${tokenValue.value}/sboms`
    )
    const sbomDownloadUrl = computed(() =>
      `${baseUrl.value}/api/v1/tenants/${tenantId.value}/access/${tokenValue.value}/sboms/{sbom_id}/download`
    )
    const vulnFeedUrl = computed(() =>
      `${baseUrl.value}/api/v1/tenants/${tenantId.value}/access/${tokenValue.value}/vulns`
    )

    const copy = (text) => {
      navigator.clipboard.writeText(text).then(() => {
        store.dispatch('notification/showNotification', { type: 'success', message: 'Copied to clipboard', duration: 1500 })
      })
    }

    const statusClass = (s) => ({
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300': s === 'pending',
      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': s === 'approved',
      'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300': s === 'revoked',
    })

    const formatDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : ''

    onMounted(fetchGrants)

    return {
      loading, createLoading, actionLoading,
      grants, currentPage, totalPages, statusFilter,
      createModal, tokenModal, activeGrant, activeToken,
      deleteTarget, revokeTarget,
      products, productsLoading,
      form,
      fetchGrants, openCreate, createGrant,
      approve, confirmRevoke, confirmDelete, rotateToken, showToken,
      sbomListUrl, sbomDownloadUrl, vulnFeedUrl, tokenRevealed, copy,
      statusClass, formatDate,
    }
  }
}
</script>
